import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { DomainStatsWithComparison } from '../../hooks/UseDomainsWithSourcesStatsComparison';
import { PaginationData } from '../../../../shared/models/monitor/data/sources/PaginationData';
import {
  calculatePaginationValues,
  createPaginationHandler,
} from '../../../../shared/helpers/PaginationHelper';
import CustomTooltip from '../../../../shared/components/common/tooltip/CustomTooltip';
import CustomLink from '../../../../shared/components/custom-link/CustomLink';
import './DomainsWithSourcesStatsTable.scss';
import { DataTable } from '../../../../shared/components/common/data-table/DataTable';
import EmptyPanel from '../../../../shared/components/empty-panel/EmptyPanel';
import { Column } from 'primereact/column';
import { TrendTag } from '../../../projects/preview/components/trend-tag/TrendTag';
import { getTrendValue } from '../../../../shared/utils/utils';
import { Skeleton } from 'primereact/skeleton';
import { KeywordResponse } from '../../../../shared/models/monitor/KeywordResponse';
import { getKeywordMap } from '../../../prompt/preview/components/stats/helpers/StatsHelper';
import KeywordTags from '../../../../shared/components/keyword-tags/KeywordTags';
import { getVerifiedBrandStatus, isFirmMentionedInBrands } from '../../helpers/SourcesHelper';
import { MetadataBadge } from '../../../../shared/components/metadata-badge/MetadataBadge';
import { Button } from 'primereact/button';
import { SearchFilter } from '../../../../shared/components/search-filter/SearchFilter';
import { TagsResponse } from '../../../../shared/models/tags/TagsResponse';
import { TAG_NAME, encodeSourceHash } from '../../../../shared/utils/urls';
import { getTagLabel } from '../../../../shared/helpers/tags/TagsHelper';
import { SOURCES } from '../../../../shared/routes/path';
import useIsMobile from '../../../../shared/hooks/global/UseIsMobile';
import { stackColumnHeader } from '../../../../shared/helpers/ColumnHelper';

interface DomainsWithSourcesStatsTableProps {
  domains: DomainStatsWithComparison[];
  pagination?: PaginationData;
  keyword?: string;
  startDate: Date | null;
  endDate: Date | null;
  previousStartDate: Date | null;
  previousEndDate: Date | null;
  onPageChange: (page: number, pageSize: number) => void;
  onKeywordChange: (keyword: string) => void;
  isLoading: boolean;
  hasAdvancedFeatures?: boolean;
  commonKeywords: KeywordResponse[];
  firmName: string;
  tags?: TagsResponse[];
  canExport: boolean | null;
  onExport: () => Promise<void>;
  hasDomainDetailPage?: boolean;
}

export const DomainsWithSourcesStatsTable: React.FC<DomainsWithSourcesStatsTableProps> = ({
  domains,
  pagination,
  keyword = '',
  startDate,
  endDate,
  previousStartDate,
  previousEndDate,
  onPageChange,
  onKeywordChange,
  isLoading,
  hasAdvancedFeatures = false,
  commonKeywords,
  firmName,
  tags = [],
  canExport,
  onExport,
  hasDomainDetailPage = false,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId, period, chat } = useParams();
  const [expandedRows, setExpandedRows] = useState<any>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const handlePageChange = createPaginationHandler(pagination, onPageChange);

  const { first, rows, totalRecords } = calculatePaginationValues(pagination, domains.length);

  const keywordsMap = useMemo(() => {
    const map = getKeywordMap(commonKeywords);
    for (const kw of commonKeywords) {
      if (kw.normalizedKeyword.startsWith(TAG_NAME) && kw.tagId) {
        map.set(kw.normalizedKeyword, getTagLabel(tags, kw.tagId, kw.normalizedKeyword));
      }
    }
    return map;
  }, [commonKeywords, tags]);

  const formatDateRange = (start: Date | null, end: Date | null): string => {
    if (!start || !end) return '';
    return `${format(start, 'dd.MM')} - ${format(end, 'dd.MM.yyyy')}`;
  };

  // Generate keys for columns with dates to force re-render when dates change
  const currentPeriodKey =
    startDate && endDate ? `${startDate.getTime()}-${endDate.getTime()}` : 'no-dates';
  const previousPeriodKey =
    previousStartDate && previousEndDate
      ? `${previousStartDate.getTime()}-${previousEndDate.getTime()}`
      : 'no-dates';

  const navigateToSource = (source: string) => {
    const hash = encodeSourceHash(source);
    navigate(`${SOURCES}/${projectId}/source/${hash}/${period || 'daily'}/${chat || 'all'}${location.search}`);
  };

  const rowExpansionTemplate = (data: DomainStatsWithComparison, domainOptions: any) => {
    return (
      <DataTable
        stripedRows
        paginatorClassName="pagination"
        tableStyle={{ tableLayout: 'fixed' }}
        value={data.sources}
        tableClassName="table"
        className="table-wrapper--stack"
        responsiveLayout="stack"
      >
        <Column
          field="source"
          header={t('source')}
          headerClassName="table__header"
          bodyClassName="table__cell--stack-title"
          body={(item, options: any) => (
            <div className="source-data">
              <span
                className="source-data__icon"
                style={{
                  backgroundImage: `url("https://favicon.is/${item.source}")`,
                }}
              />
              <div className={`source-data__details${!hasDomainDetailPage ? ' source-data__details--basic' : ''}`}>
                {item.title && (
                  hasDomainDetailPage ? (
                    <a
                      className="source-data__title source-data__title--clickable text-ellipsis"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigateToSource(item.source);
                      }}
                    >
                      {item.title}
                    </a>
                  ) : (
                    <span className="source-data__title text-ellipsis">{item.title}</span>
                  )
                )}
                <span
                  className="source-data__url text-ellipsis"
                  style={!item.title ? { marginTop: '2px' } : undefined}
                >
                  {hasDomainDetailPage ? (
                    <span className="source-data__source-with-link">
                      <a
                        className="custom-link custom-link--clickable text-ellipsis"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigateToSource(item.source);
                        }}
                      >
                        {item.source}
                      </a>
                      <a
                        className="source-data__external-link"
                        href={item.source.startsWith('http') ? item.source : `https://${item.source}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span className="ico--sm ico--external-link" />
                      </a>
                    </span>
                  ) : (
                    <CustomLink url={item.source} target="_blank" />
                  )}
                </span>
                {hasAdvancedFeatures && (
                  <KeywordTags
                    keywordsString={item.brands}
                    keywordsMap={keywordsMap}
                    containerWidth={230}
                    minVisibleKeywords={5}
                    uniqueId={`domain-${domainOptions.rowIndex}-source-${options.rowIndex}`}
                  />
                )}
              </div>
            </div>
          )}
          style={{ minWidth: '245px' }}
        />
        {hasAdvancedFeatures && (
          <Column
            field="isOwnBrandMentioned"
            header={
              isMobile ? (
                t('sourcesOwnBrand')
              ) : (
                <div className="cell-header__title cell-header__title--wrap">
                  <div style={{ textTransform: 'capitalize', fontWeight: 600 }}>{firmName}</div>
                  <div>
                    <span id={`sources-own-brand-header-${domainOptions.rowIndex}`}>
                      {t('sourcesOwnBrand')}
                    </span>
                    <CustomTooltip
                      tooltipText={t('sourcesOwnBrandHeaderTooltip', { firmName })}
                      targetRef={`sources-own-brand-header-${domainOptions.rowIndex}`}
                    />
                  </div>
                </div>
              )
            }
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            style={{ width: '80px', minWidth: '80px' }}
            body={(item, options: any) => {
              const { variant, statusText, tooltipText } = getVerifiedBrandStatus(
                item.isVerified,
                isFirmMentionedInBrands(item.brands, firmName, tags),
                firmName,
                t
              );

              return (
                <MetadataBadge
                  variant={variant}
                  text={statusText}
                  tooltipText={tooltipText}
                  uniqueId={`brand-tooltip-domain-${domainOptions.rowIndex}-${options.rowIndex}`}
                />
              );
            }}
          />
        )}
        <Column
          key={`previous-share-${previousPeriodKey}`}
          header={
            isMobile ? (
              stackColumnHeader(t('sourcesAnalyticsShare'), previousStartDate, previousEndDate)
            ) : (
              <div className="cell-header">
                <div className="cell-header__title">
                  <span id="source-prev-share-header">{t('sourcesAnalyticsShare')}</span>
                  <CustomTooltip
                    tooltipText="sourcesAnalyticsShareTooltip"
                    targetRef="source-prev-share-header"
                  />
                </div>
                {previousStartDate && previousEndDate && (
                  <div className="cell-header__details">
                    {formatDateRange(previousStartDate, previousEndDate)}
                  </div>
                )}
              </div>
            )
          }
          headerClassName="table__header table__header--center table__header--relative"
          bodyClassName="table__cell table__cell--center table__cell--grey"
          style={{ width: '70px' }}
          body={(rowData) => `${rowData.previousShare.toFixed(1)}%`}
        />
        <Column
          key={`current-share-${currentPeriodKey}`}
          header={
            isMobile ? (
              stackColumnHeader(t('sourcesAnalyticsShare'), startDate, endDate)
            ) : (
              <div className="cell-header">
                <div className="cell-header__title">
                  <span id="source-curr-share-header">{t('sourcesAnalyticsShare')}</span>
                  <CustomTooltip
                    tooltipText="sourcesAnalyticsShareTooltip"
                    targetRef="source-curr-share-header"
                  />
                </div>
                {startDate && endDate && (
                  <div className="cell-header__details">{formatDateRange(startDate, endDate)}</div>
                )}
              </div>
            )
          }
          headerClassName="table__header table__header--center table__header--relative"
          bodyClassName="table__cell table__cell--center"
          style={{ width: '70px' }}
          body={(rowData) => `${rowData.share.toFixed(1)}%`}
        />
        <Column
          header={
            isMobile ? (
              t('sourcesAnalyticsShareChange')
            ) : (
              <div className="cell-header__title">
                <span id="source-share-change-header">{t('sourcesAnalyticsShareChange')}</span>
                <CustomTooltip
                  tooltipText="sourcesAnalyticsShareChangeTooltip"
                  targetRef="source-share-change-header"
                />
              </div>
            )
          }
          headerClassName="table__header table__header--center"
          bodyClassName="table__cell table__cell--center"
          style={{ width: '65px' }}
          body={(rowData) => <TrendTag trendValue={getTrendValue(rowData.shareChange, 2)} suffix="%" />}
        />
        <Column
          key={`previous-prompts-${previousPeriodKey}`}
          header={
            isMobile ? (
              stackColumnHeader(t('sourcesAnalyticsPrompts'), previousStartDate, previousEndDate)
            ) : (
              <div className="cell-header">
                <div className="cell-header__title">
                  <span id="source-prev-prompts-header">{t('sourcesAnalyticsPrompts')}</span>
                  <CustomTooltip
                    tooltipText="sourcesAnalyticsPromptsTooltip"
                    targetRef="source-prev-prompts-header"
                  />
                </div>
                {previousStartDate && previousEndDate && (
                  <div className="cell-header__details">
                    {formatDateRange(previousStartDate, previousEndDate)}
                  </div>
                )}
              </div>
            )
          }
          headerClassName="table__header table__header--center table__header--relative"
          bodyClassName="table__cell table__cell--center table__cell--grey"
          style={{ width: '70px' }}
          body={(rowData) => rowData.previousPromptCount}
        />
        <Column
          key={`current-prompts-${currentPeriodKey}`}
          header={
            isMobile ? (
              stackColumnHeader(t('sourcesAnalyticsPrompts'), startDate, endDate)
            ) : (
              <div className="cell-header">
                <div className="cell-header__title">
                  <span id="source-curr-prompts-header">{t('sourcesAnalyticsPrompts')}</span>
                  <CustomTooltip
                    tooltipText="sourcesAnalyticsPromptsTooltip"
                    targetRef="source-curr-prompts-header"
                  />
                </div>
                {startDate && endDate && (
                  <div className="cell-header__details">{formatDateRange(startDate, endDate)}</div>
                )}
              </div>
            )
          }
          headerClassName="table__header table__header--center table__header--relative"
          bodyClassName="table__cell table__cell--center"
          style={{ width: '70px' }}
          body={(rowData) => rowData.promptCount}
        />
        <Column
          header={
            isMobile ? (
              t('sourcesAnalyticsPromptsChange')
            ) : (
              <div className="cell-header__title">
                <span id="source-prompts-change-header">{t('sourcesAnalyticsPromptsChange')}</span>
                <CustomTooltip
                  tooltipText="sourcesAnalyticsPromptsChangeTooltip"
                  targetRef="source-prompts-change-header"
                />
              </div>
            )
          }
          headerClassName="table__header table__header--center"
          bodyClassName="table__cell table__cell--center"
          style={{ width: '50px' }}
          body={(rowData) => (
            <TrendTag trendValue={getTrendValue(rowData.promptCountChange)} suffix="" />
          )}
        />
      </DataTable>
    );
  };

  const handleExport = async (): Promise<void> => {
    if (isDownloading || !canExport) {
      return;
    }

    setIsDownloading(true);
    try {
      await onExport();
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="stats__panel">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('domainsWithSourcesAnalytics')}
          <CustomTooltip tooltipText="domainsWithSourcesAnalyticsTooltip" addMargin={false} />
        </div>
        <div className="page-box__source-filter">
          <SearchFilter value={keyword} onChange={onKeywordChange} className="search-filter--inline" />
          {/* Przy pustej tabeli nie ma czego eksportować. */}
          {domains.length > 0 && (
          <>
            <Button
              id="btn-export-domains-stats"
              className={`action-btn ${!canExport ? 'btn--disabled-tooltip' : ''}`}
              onClick={handleExport}
            >
              {isDownloading ? (
                <span className="ico--loader ico--xs" />
              ) : (
                <span className={`ico--download ${!canExport ? 'ico--disabled' : ''} ico--xs`} />
              )}
            </Button>
            <CustomTooltip
              tooltipText={!canExport ? 'upgradePlanTrialExportMsg' : 'downloadDomains'}
              targetRef="btn-export-domains-stats"
            />
          </>
          )}
        </div>
      </div>
      {isLoading ? (
        <Skeleton className="skeleton--table">
          <span />
          <span />
          <span />
          <span />
          <span />
          <span />
          <span />
        </Skeleton>
      ) : (
        <DataTable
          stripedRows
          paginator
          lazy
          paginatorClassName="pagination"
          first={first}
          rows={rows}
          totalRecords={totalRecords}
          rowsPerPageOptions={[10, 25, 50]}
          onPage={handlePageChange}
          removableSort
          sortField="created"
          sortOrder={-1}
          value={domains}
          tableStyle={{ width: '100%' }}
          tableClassName="table"
          className="table-wrapper table-wrapper--accordion table-wrapper--stack domains-with-sources-table"
          responsiveLayout="stack"
          expandedRows={expandedRows}
          emptyMessage={() => <EmptyPanel />}
          onRowToggle={(e) => setExpandedRows(e.data)}
          rowExpansionTemplate={rowExpansionTemplate}
          dataKey="domain"
          currentPageReportTemplate={t('rowsNumber') + ':'}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        >
          <Column
            field="domain"
            header={t('domain')}
            headerClassName="table__header"
            bodyClassName="table__cell--stack-title"
            style={{ minWidth: '245px', width: 'auto' }}
            body={(item) => (
              <div className="source-data">
                <span
                  className="source-data__icon"
                  style={{
                    backgroundImage: `url("https://favicon.is/${item.domain}")`,
                  }}
                />
                {hasDomainDetailPage ? (
                  <span className="source-data__domain-with-link">
                    <a
                      className="custom-link custom-link--clickable text-ellipsis"
                      onClick={(e) => {
                        e.stopPropagation();
                        const encodedDomain = encodeURIComponent(item.domain);
                        navigate(`${SOURCES}/${projectId}/domain/${encodedDomain}/${period || 'daily'}/${chat || 'all'}${location.search}`);
                      }}
                    >
                      {item.domain}
                    </a>
                    <a
                      className="source-data__external-link"
                      href={`https://${item.domain}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span className="ico--sm ico--external-link" />
                    </a>
                  </span>
                ) : (
                  <span className="text-ellipsis" style={{ width: '100%' }}>
                    <CustomLink url={item.domain} target="_blank" addHttps={true} />
                  </span>
                )}
              </div>
            )}
          />
          <Column
            key={`previous-share-${previousPeriodKey}`}
            header={
              isMobile ? (
                stackColumnHeader(t('domainsWithSourcesAnalyticsShare'), previousStartDate, previousEndDate)
              ) : (
                <div className="cell-header">
                  <div className="cell-header__title">
                    <span id="domain-prev-share-header">{t('domainsWithSourcesAnalyticsShare')}</span>
                    <CustomTooltip
                      tooltipText="domainsWithSourcesAnalyticsShareTooltip"
                      targetRef="domain-prev-share-header"
                    />
                  </div>
                  {previousStartDate && previousEndDate && (
                    <div className="cell-header__details">
                      {formatDateRange(previousStartDate, previousEndDate)}
                    </div>
                  )}
                </div>
              )
            }
            headerClassName="table__header table__header--center table__header--relative"
            bodyClassName="table__cell table__cell--center table__cell--grey"
            style={{ width: '70px' }}
            body={(rowData) => `${rowData.previousShare.toFixed(1)}%`}
          />
          <Column
            key={`current-share-${currentPeriodKey}`}
            header={
              isMobile ? (
                stackColumnHeader(t('domainsWithSourcesAnalyticsShare'), startDate, endDate)
              ) : (
                <div className="cell-header">
                  <div className="cell-header__title">
                    <span id="domain-curr-share-header">{t('domainsWithSourcesAnalyticsShare')}</span>
                    <CustomTooltip
                      tooltipText="domainsWithSourcesAnalyticsShareTooltip"
                      targetRef="domain-curr-share-header"
                    />
                  </div>
                  {startDate && endDate && (
                    <div className="cell-header__details">{formatDateRange(startDate, endDate)}</div>
                  )}
                </div>
              )
            }
            headerClassName="table__header table__header--center table__header--relative"
            bodyClassName="table__cell table__cell--center"
            style={{ width: '70px' }}
            body={(rowData) => `${rowData.share.toFixed(1)}%`}
          />
          <Column
            header={
              isMobile ? (
                t('domainsWithSourcesAnalyticsShareChange')
              ) : (
                <div className="cell-header__title">
                  <span id="domain-share-change-header">
                    {t('domainsWithSourcesAnalyticsShareChange')}
                  </span>
                  <CustomTooltip
                    tooltipText="domainsWithSourcesAnalyticsShareChangeTooltip"
                    targetRef="domain-share-change-header"
                  />
                </div>
              )
            }
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell table__cell--right-border table__cell--center"
            style={{ width: '65px' }}
            body={(rowData) => (
              <TrendTag trendValue={getTrendValue(rowData.shareChange, 2)} suffix="%" />
            )}
          />
          <Column
            key={`previous-prompts-${previousPeriodKey}`}
            header={
              isMobile ? (
                stackColumnHeader(t('domainsWithSourcesAnalyticsPrompts'), previousStartDate, previousEndDate)
              ) : (
                <div className="cell-header">
                  <div className="cell-header__title">
                    <span id="domain-prev-prompts-header">{t('domainsWithSourcesAnalyticsPrompts')}</span>
                    <CustomTooltip
                      tooltipText="domainsWithSourcesAnalyticsPromptsTooltip"
                      targetRef="domain-prev-prompts-header"
                    />
                  </div>
                  {previousStartDate && previousEndDate && (
                    <div className="cell-header__details">
                      {formatDateRange(previousStartDate, previousEndDate)}
                    </div>
                  )}
                </div>
              )
            }
            headerClassName="table__header table__header--center table__header--relative"
            bodyClassName="table__cell table__cell--center table__cell--grey"
            style={{ width: '70px' }}
            body={(rowData) => rowData.previousPromptCount}
          />
          <Column
            key={`current-prompts-${currentPeriodKey}`}
            header={
              isMobile ? (
                stackColumnHeader(t('domainsWithSourcesAnalyticsPrompts'), startDate, endDate)
              ) : (
                <div className="cell-header">
                  <div className="cell-header__title">
                    <span id="domain-curr-prompts-header">{t('domainsWithSourcesAnalyticsPrompts')}</span>
                    <CustomTooltip
                      tooltipText="domainsWithSourcesAnalyticsPromptsTooltip"
                      targetRef="domain-curr-prompts-header"
                    />
                  </div>
                  {startDate && endDate && (
                    <div className="cell-header__details">{formatDateRange(startDate, endDate)}</div>
                  )}
                </div>
              )
            }
            headerClassName="table__header table__header--center table__header--relative"
            bodyClassName="table__cell table__cell--center"
            style={{ width: '70px' }}
            body={(rowData) => rowData.promptCount}
          />
          <Column
            header={
              isMobile ? (
                t('domainsWithSourcesAnalyticsPromptsChange')
              ) : (
                <div className="cell-header__title">
                  <span id="domain-prompts-change-header">
                    {t('domainsWithSourcesAnalyticsPromptsChange')}
                  </span>
                  <CustomTooltip
                    tooltipText="domainsWithSourcesAnalyticsPromptsChangeTooltip"
                    targetRef="domain-prompts-change-header"
                  />
                </div>
              )
            }
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell table__cell--center"
            style={{ width: '50px' }}
            body={(rowData) => (
              <TrendTag trendValue={getTrendValue(rowData.promptCountChange)} suffix="" />
            )}
          />
          <Column
            expander={true}
            bodyClassName="table__cell--stack-actions"
            style={{ width: '25px' }}
            frozen={true}
            alignFrozen="right"
          />
        </DataTable>
      )}
    </div>
  );
};
